/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import { Text, Flex } from 'components/ui';
import InfoButton from 'components/InfoButton/InfoButton';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import checkoutUtils from 'utils/Checkout';
import localeUtils from 'utils/LanguageLocale';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import CanadaPostStrikeCheckoutMessage from 'components/SharedComponents/CanadaPostStrike/CanadaPostStrikeCheckoutMessage';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import { FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import store from 'store/Store';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import agentAwareUtils from 'utils/AgentAware';

import ShippingGroups from 'components/FrictionlessCheckout/ShippingMethods/ShippingGroups';
import ShippingMethod from 'components/FrictionlessCheckout/ShippingMethods/ShippingMethod';
import { globalModals, renderModal } from 'utils/globalModals';

const { checkPromoEligibility, setShippingMethod } = FrictionlessUtils;
const { SHIPPING_METHOD_RESTRICTIONS } = globalModals;
const STANDARD = !localeUtils.isCanada() ? 'Standard' : localeUtils.isFrench() ? 'gratuite' : 'Free';
const isFCReleaseThreeEnabled = FrictionlessUtils.isFCReleaseThreeEnabled();

class ShippingMethods extends BaseClass {
    state = {
        isEditView: false,
        shippingMethodSelected: this.props.shippingMethodSelected,
        message: false
    };

    componentDidMount() {
        const { isSplitEDDEnabled, shippingGroup, isElectronicShippingGroup } = this.props;
        const showSplitEDD = isSplitEDDEnabled && !isElectronicShippingGroup && checkoutUtils.hasDeliveryGroups([shippingGroup?.shippingMethod]);
        Storage.local.setItem(LOCAL_STORAGE.SPLIT_EDD_EXPERIENCE, showSplitEDD);
    }

    componentDidUpdate(prevProps) {
        const prevShippingMethodId = prevProps.shippingMethodSelected?.shippingMethodId;
        const currentShippingMethodId = this.props.shippingMethodSelected?.shippingMethodId;

        if (
            prevShippingMethodId !== currentShippingMethodId ||
            (!this.props.isEditMode && prevProps.isEditMode && currentShippingMethodId !== this.state.shippingMethodSelected.shippingMethodId)
        ) {
            this.setState({
                shippingMethodSelected: this.props.shippingMethodSelected
            });
        }
    }

    getShippingFee = shippingFee => {
        return checkoutUtils.isZeroFee(shippingFee) ? this.props.locales.free : shippingFee;
    };

    getShippingSuffix = shippingMethodType => {
        const isStandardShipping = shippingMethodType?.toLowerCase().startsWith('standard');

        return !localeUtils.isCanada() && !isStandardShipping ? ` ${this.props.locales.shipping}` : '';
    };

    onEditClick = () => {
        if (isFCReleaseThreeEnabled) {
            const path = `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.SHIPPING_METHODS.path}`;

            this.props.goToURL({ path });
        } else {
            this.setState({
                isEditView: true
            });
        }

        FrictionlessCheckoutBindings.setChangeLinkAnalytics(anaConsts.PAGE_TYPES.DELIVERY, true);
        this.props.setActiveSection(this.props.sectionName);
        this.props.setShippingMehthodNeedsConfirmation(true);
    };

    fireShippingMethodChangeAnalytics = () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                sTagInfo: {
                    ...this.state?.shippingMethodSelected,
                    actionInfo: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_UPDATE,
                    linkName: anaConsts.LinkData.CHECKOUT_SHIPPING_METHOD_UPDATE
                }
            }
        });
    };

    setShippingMethod = () => {
        if (checkPromoEligibility(this.state.shippingMethodSelected, this.props.orderDetails)) {
            setShippingMethod({
                orderId: this.props.orderId,
                newShippingMethodId: this.state.shippingMethodSelected.shippingMethodId,
                shippingGroupId: this.props.shippingGroup?.shippingGroupId,
                successCallback: this.onSaveSuccessCallback,
                failureCallback: () => {},
                message: this.state.message,
                updateWaiveShipping: this.props.updateWaiveShipping
            });
            this.fireShippingMethodChangeAnalytics();
        }
    };

    onSaveSuccessCallback = () => {
        this.setState(
            {
                isEditView: false
            },
            () => {
                this.props.setShippingMehthodNeedsConfirmation(false);
                this.props.refreshCheckout().then(() => {
                    FrictionlessUtils.updateCheckoutURLBasedOnOrderCompleteness();
                });
            }
        );
    };

    handleCheckboxChange = e => this.setState({ message: e.target.checked });

    showAutoReplenishModal = () => {
        const {
            infoModalData: { sid, title, width }
        } = this.props;

        const pageType = anaConsts.PAGE_TYPES.AUTO_REPLENISH;
        const pageDetail = anaConsts.PAGE_DETAIL.SUBSCRIPTION_INFO;
        const prop55 = anaConsts.LinkData.AUTO_REPLENISH;

        this.props.showContentModal({
            isOpen: true,
            data: {
                sid,
                title,
                width,
                isPrescreenModal: false
            },
            analyticsData: {
                pageName: `${pageType}:${pageDetail}:n/a:*`,
                linkData: prop55
            }
        });
    };

    openAutoReplenishProductsModal = () => {
        this.props.showAutoReplenishProductsModal({
            isOpen: true
        });
    };

    handleOnClickShippingMethodSelected = shippingMethod => {
        this.setState({
            shippingMethodSelected: shippingMethod
        });
    };

    openShippingRestrictionsModal = () => {
        renderModal(this.props.globalModals[SHIPPING_METHOD_RESTRICTIONS]);
    };

    render() {
        const {
            sectionNumber,
            shippingGroup,
            standardBasket,
            locales,
            isChangePermitted,
            shippingMethods,
            isNewUserFlow,
            isReplenOnly,
            shippingGroupType,
            middleZone,
            sectionLevelError,
            isElectronicShippingGroup,
            isPhysicalGiftCard,
            orderHasReplen,
            isSdd,
            isSDDAndGiftCardOnly,
            standardItemsQuantity,
            replenishItemsQuantity,
            isSplitEDDEnabled,
            containsHazmatItems
        } = this.props;
        let shipFee = this.getShippingFee(shippingGroup?.shippingMethod?.shippingFee);

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            const orderInfo = store.getState().order;

            if (orderInfo.waiveShippingFee) {
                shipFee = locales.waived;
            }
        }

        let changeLabel = null;

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            changeLabel = locales.changeShippingMethod;
        }

        const isEditMode = isFCReleaseThreeEnabled ? this.props.isEditMode : this.state.isEditView;

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber: !sectionLevelError && sectionNumber,
                    title: locales.title,
                    hasDivider: true,
                    isChangePermitted:
                        (isFCReleaseThreeEnabled && this.props.isEditMode) || (this.state.isEditView && !isFCReleaseThreeEnabled)
                            ? false
                            : isChangePermitted,
                    onChangeClick: this.onEditClick,
                    coustomChangeLabel: changeLabel,
                    sectionLevelError,
                    sectionName: SECTION_NAMES.SHIPPING_METHOD
                }}
                isEditMode={isEditMode}
                marginTop={[4, 4, 5]}
                hasPaddingForChildren={false}
                isCollapsed={isNewUserFlow}
                ariaLabel={locales.title}
                role='region'
            >
                <CanadaPostStrikeCheckoutMessage
                    isFrictionlessCheckout={true}
                    shippingGroup={shippingGroup}
                    middleZone={middleZone}
                />
                {isEditMode ? (
                    <>
                        {containsHazmatItems && isFCReleaseThreeEnabled && (
                            <Flex
                                marginX={[4, 5]}
                                alignItems='center'
                                marginY={4}
                                gap={1}
                            >
                                <Text
                                    is='p'
                                    children={locales.shippingRestrictions}
                                />
                                <InfoButton
                                    size={16}
                                    onClick={this.openShippingRestrictionsModal}
                                />
                            </Flex>
                        )}
                        {shippingMethods?.map((shippingMethod, index) => (
                            <ShippingMethod
                                key={shippingMethod.shippingMethodId || index}
                                locales={locales}
                                isReplenOnly={isReplenOnly}
                                shippingMethod={shippingMethod}
                                isLastItem={index === shippingMethods.length - 1}
                                shippingGroup={shippingGroup}
                                shippingGroupType={shippingGroupType}
                                handleOnClickShippingMethodSelected={this.handleOnClickShippingMethodSelected}
                                getShippingFee={this.getShippingFee}
                                shippingMethodSelected={this.state.shippingMethodSelected}
                                isSplitEDDEnabled={isSplitEDDEnabled}
                                orderHasReplen={orderHasReplen}
                                standardItemsQuantity={standardItemsQuantity}
                                replenishItemsQuantity={replenishItemsQuantity}
                                isPhysicalGiftCard={isPhysicalGiftCard}
                                isElectronicShippingGroup={isElectronicShippingGroup}
                                isEditView={isEditMode}
                                showAutoReplenishModal={this.showAutoReplenishModal}
                                openAutoReplenishProductsModal={this.openAutoReplenishProductsModal}
                                standardBasket={standardBasket}
                                message={this.state.message}
                                handleCheckboxChange={this.handleCheckboxChange}
                                setShippingMethod={this.setShippingMethod}
                            />
                        ))}
                    </>
                ) : (
                    <>
                        {!isElectronicShippingGroup && (
                            <Text
                                is='h2'
                                fontSize='md'
                                fontWeight='bold'
                                paddingX={[4, 4, 5]}
                                children={`${
                                    isReplenOnly && shippingGroup?.shippingMethod?.shippingMethodType === STANDARD
                                        ? locales.shippingMethodType
                                        : shippingGroup?.shippingMethod?.shippingMethodType
                                }${this.getShippingSuffix(shippingGroup?.shippingMethod?.shippingMethodType)} • ${shipFee}`}
                            />
                        )}
                        {standardBasket?.items?.length > 0 && (
                            <ShippingGroups
                                locales={locales}
                                isSdd={isSdd}
                                isSDDAndGiftCardOnly={isSDDAndGiftCardOnly}
                                isReplenOnly={isReplenOnly}
                                isElectronicShippingGroup={isElectronicShippingGroup}
                                isPhysicalGiftCard={isPhysicalGiftCard}
                                standardItemsQuantity={standardItemsQuantity}
                                shippingGroup={shippingGroup}
                                shippingGroupType={shippingGroupType}
                                orderHasReplen={orderHasReplen}
                                replenishItemsQuantity={replenishItemsQuantity}
                                isEditView={isEditMode}
                                showAutoReplenishModal={this.showAutoReplenishModal}
                                openAutoReplenishProductsModal={this.openAutoReplenishProductsModal}
                            />
                        )}
                    </>
                )}
            </LayoutCard>
        );
    }
}

export default wrapComponent(ShippingMethods, 'ShippingMethods', true);
